import React, { useMemo, useState } from 'react';

import { displayAxiosError } from '@apis/next/errors';
import { dashboardVesselScheduleApis } from '@apis/next/vesselScheduleApis';
import { TextInput } from '@components/NextForms/TextInput';
import { cellFactory, Table } from '@components/Table/Table';
import { vesselScheduleStrings } from '@data/vesselSchedule';
import { useVesselAutoComplete } from '@hooks/next/useVesselAutoComplete';
import { useAgency } from '@hooks/queries/dashboard/useAgency';
import { useUser } from '@hooks/queries/useUser';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import {
  VesselScheduleHelper,
  VesselScheduleItem,
  VesselScheduleItemForm,
} from '@models/next/VesselSchedule';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { formatDate, mapOptionalOr } from '@utils/types';
import { isErrorFromPath } from '@zodios/core';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';

const useStyles = makeMuiStyles((_theme, _common) => ({
  input: {
    width: 0,
  },
  dateInput: {
    width: 80,
  },
  row: {
    verticalAlign: 'top',
    '& td:nth-child(-n+2)': {
      paddingTop: 19,
    },
    '& td:nth-child(n+12)': {
      paddingTop: 19,
    },
  },
}));

export interface VesselScheduleDisplayEditRowProps {
  index: number;
  item: VesselScheduleItem;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
}

export const VesselScheduleDisplayEditRow: React.FC<VesselScheduleDisplayEditRowProps> = React.memo(
  observer((props) => {
    const classes = {
      ...useCommonStyles(),
      ...useStyles(),
    };

    const { index, item, checked, onCheckedChange, editing, onEditingChange } = props;

    const form = useForm(VesselScheduleItemForm, {
      initialOutput: item,
    });

    const {
      root: { fields },
    } = form;

    const patchVesselSchedule = dashboardVesselScheduleApis.usePatch(
      '/dashboard/vessel-schedule/:id',
      {
        params: { id: item.id },
      },
    );

    const autoCompleteConfigs = useVesselAutoComplete(fields.vessel, fields.voyage, editing);

    const { confirm, renderDialog } = useConfirmDialog();

    const user = useUser();

    const agency = useAgency(user.data?.agency_id!, { enabled: !!user.data?.agency_id });

    const canWrite = useMemo(() => {
      if (item && user.data && (agency.isSuccess || !agency.isFetching)) {
        return VesselScheduleHelper.canWriteVesselSchedule(item, user.data, agency.data);
      }
    }, [item, user.data, agency.data]);

    const [optimisticItem, setOptimisticItem] = useState(item);

    const onSubmit = async () => {
      await form.handleSubmit(async () => {
        if (!form.parsed.success) {
          return;
        }
        const values = form.parsed.data;

        try {
          onEditingChange(false);
          setOptimisticItem({
            ...item,
            ...values,
          });
          await patchVesselSchedule.mutateAsync(values);
        } catch (e) {
          setOptimisticItem(item);
          setTimeout(() => {
            onEditingChange(true);
          }, 500);

          if (
            isErrorFromPath(
              dashboardVesselScheduleApis.client.api,
              'patch',
              '/dashboard/vessel-schedule/:id',
              e,
            ) &&
            e.response.status === 406
          ) {
            await confirm({
              message: vesselScheduleStrings.cannotUpdateVessel(e.response.data),
            });
          } else {
            await confirm({
              message: displayAxiosError(e),
            });
          }
        }
      });
    };

    if (editing) {
      return (
        <>
          {renderDialog()}

          <Table.Row
            className={classes.row}
            columns={[
              cellFactory.createCheckable(checked, onCheckedChange),
              cellFactory.create(index),
              cellFactory.create(
                <TextInput
                  field={fields.vessel}
                  autoCompleteConfig={autoCompleteConfigs.vessel}
                  popperConfig={{ width: 'max-content' }}
                />,
              ),
              cellFactory.create(
                <TextInput
                  field={fields.callsign}
                  autoCompleteConfig={autoCompleteConfigs.callsign}
                  popperConfig={{ width: 'max-content' }}
                />,
              ),
              cellFactory.create(<TextInput field={fields.voyage} />),
              cellFactory.create(<TextInput field={fields.pol} />),
              cellFactory.create(<TextInput field={fields.pod} />),
              cellFactory.create(
                <TextInput
                  field={fields.etd}
                  inputProps={{ placeholder: formatDate(dayjs())! }}
                  withErrorMessages={false}
                />,
              ),
              cellFactory.create(
                <TextInput
                  field={fields.eta}
                  inputProps={{ placeholder: formatDate(dayjs().add(1, 'day'))! }}
                  withErrorMessages={false}
                />,
              ),
              cellFactory.create(
                <TextInput
                  field={fields.atd}
                  inputProps={{ placeholder: formatDate(dayjs().add(1, 'day'))! }}
                  withErrorMessages={false}
                />,
              ),
              cellFactory.create(
                <TextInput
                  field={fields.ata}
                  inputProps={{ placeholder: formatDate(dayjs().add(2, 'day'))! }}
                  withErrorMessages={false}
                />,
              ),
              cellFactory.create(
                <a className={clsx(classes.link, classes.baseButton)} onClick={onSubmit}>
                  {formStrings.update}
                </a>,
              ),
            ]}
          />
        </>
      );
    } else {
      const renderMaybeDate = (date: string | null | undefined) =>
        mapOptionalOr(date, formatDate, () => '-');

      return (
        <>
          {renderDialog()}
          <Table.Row
            columns={[
              cellFactory.createCheckable(checked, onCheckedChange),
              cellFactory.create(index),
              cellFactory.create(
                <span className={classes.whitespacePre}>{optimisticItem.vessel || '-'}</span>,
              ),
              cellFactory.create(
                <span className={classes.whitespacePre}>{optimisticItem.callsign || '-'}</span>,
              ),
              cellFactory.create(
                <span className={classes.whitespacePre}>{optimisticItem.voyage || '-'}</span>,
              ),
              cellFactory.create(
                <span className={classes.whitespacePre}>{optimisticItem.pol || '-'}</span>,
              ),
              cellFactory.create(
                <span className={classes.whitespacePre}>{optimisticItem.pod || '-'}</span>,
              ),
              cellFactory.create(
                <span className={classes.whitespacePre}>
                  {renderMaybeDate(optimisticItem.etd)}
                </span>,
              ),
              cellFactory.create(
                <span className={classes.whitespacePre}>
                  {renderMaybeDate(optimisticItem.eta)}
                </span>,
              ),
              cellFactory.create(
                <span className={classes.whitespacePre}>
                  {renderMaybeDate(optimisticItem.atd)}
                </span>,
              ),
              cellFactory.create(
                <span className={classes.whitespacePre}>
                  {renderMaybeDate(optimisticItem.ata)}
                </span>,
              ),
              cellFactory.create(
                <a
                  className={clsx(
                    classes.link,
                    (!canWrite || canWrite?._tag === 'Left') && classes.textGray400Important,
                    classes.baseButton,
                  )}
                  onClick={() => {
                    if (!canWrite) return;

                    if (canWrite._tag === 'Right') {
                      onEditingChange(true);
                    } else {
                      confirm({ message: [canWrite?.left.messages] });
                    }
                  }}
                >
                  {formStrings.edit}
                </a>,
              ),
            ]}
          />
        </>
      );
    }
  }),
);
